import React, { useEffect, useState } from 'react'
import { Text } from '../../../../../_components/Typography/Components/Text/Text'
import { FormField } from '../../../../../_components/Forms/FormField'
import { LoaderButton } from '../../../../../_components/LoaderButton'
import { AlertMessage } from '../../../../../_components/AlertMessage'
import { StatusBanner } from '../../../../../_components/StatusBanner'
import styled from 'styled-components'

import { getBusinessTerms, updateInvoiceSchedule } from '../../../../../_services/lender.service'
import { omit, isEmpty } from 'lodash'
import { formatCurrency } from 'legacy/_helpers'

export const S_SelectTerms = styled.div`
  .notice {
    padding: 10px;
  }
`
export const TermsContainer = styled.div`
  padding: 16px 0;
`

const getInvoiceScheduleTerms = (invoiceSchedule) => {
  const selectedTerms = {}
  invoiceSchedule.terms.forEach((term) => (selectedTerms[term.id] = true))

  return selectedTerms
}

export const SelectTerms = ({ business, invoiceSchedule }) => {
  // Use the current minimum_fee_per_invoice if present.
  // Otherwise, use the default minimum_factoring_fee configured on the Business.
  // This ensures that the value being displayed is the one being used when the Schedule is purchased even if
  // Business.minimum_factoring_fee has changed after setting this term in the Schedule.
  const findMinimumFeeTermValue = () => {
    const currentFee = Number(invoiceSchedule.minimum_fee_per_invoice)
    const defaultFee = Number(business.minimum_factoring_fee)
    return currentFee || defaultFee
  }

  const [saving, setSaving] = useState(false)
  const [invoiceTerms, setInvoiceTerms] = useState(null)
  const [minimumFeeTermValue, setMinimumFeeTermValue] = useState(findMinimumFeeTermValue())

  const [termOptions, setTermOptions] = useState([])
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)
  const MINIMUM_FACTORING_FEE_ID = 'minimumFactoringFee'

  const resetMessages = () => {
    setError(null)
    setSuccess(false)
  }

  const saveTerms = async () => {
    setSaving(true)
    resetMessages()
    const terms = Object.keys(invoiceTerms).map((termId) => ({ id: termId }))

    if (isEmpty(terms)) {
      setError('Please select at least one term.')
      setSaving(false)
      return
    }

    updateInvoiceSchedule(invoiceSchedule.id, { terms: terms, minimum_fee_per_invoice: minimumFeeTermValue })
      .then((result) => {
        setSuccess(true)
      })
      .catch((error) => {
        setError('An error occurred while saving the terms.')
      })
      .finally(() => {
        setSaving(false)
      })
  }

  const buildMinimumFactoringFeeOption = () => {
    return {
      id: MINIMUM_FACTORING_FEE_ID,
      name: MINIMUM_FACTORING_FEE_ID,
      label: `Minimum Factoring Fee - ${formatCurrency(findMinimumFeeTermValue())}`,
      checked: invoiceSchedule.minimum_fee_per_invoice > 0,
    }
  }

  const fetchTermOptions = async () => {
    const options = []

    if (findMinimumFeeTermValue()) options.push(buildMinimumFactoringFeeOption())

    const businessTerms = await getBusinessTerms(invoiceSchedule.business_id)
    businessTerms
      .filter((t) => t.type !== 'AdvanceRate')
      .map((term) => {
        const checked = (invoiceTerms && invoiceTerms[term.id]) || false
        options.push({ name: term.type, checked: checked, id: term.id, label: term.name || term.type })
      })
    return options
  }

  useEffect(() => {
    async function fetchSelectedTerms() {
      const selectedTerms = await getInvoiceScheduleTerms(invoiceSchedule)
      setInvoiceTerms({ ...selectedTerms })
    }
    fetchSelectedTerms()
  }, [])

  useEffect(() => {
    if (invoiceTerms === null) {
      return
    }

    fetchTermOptions().then((termOptions) => setTermOptions(termOptions))
  }, [invoiceTerms])

  const onChange = (value, id, event) => {
    if (id === MINIMUM_FACTORING_FEE_ID) {
      setMinimumFeeTermValue(value ? findMinimumFeeTermValue() : 0)
      return
    }
    const idNumber = Number(id)
    const newTerms = (value = value ? { ...invoiceTerms, [idNumber]: value } : omit(invoiceTerms, idNumber))

    setInvoiceTerms(newTerms)
  }

  return (
    <S_SelectTerms>
      <Text fs={'16px'}>{'Select Term(s) of this Schedule'}</Text>
      <div className="notice">
        {error && <AlertMessage type={'error'}>{error}</AlertMessage>}
        {success && <StatusBanner className="success" message={'Terms Saved'} />}
      </div>
      <TermsContainer>
        <FormField
          id="fee"
          label="Select Terms of this Schedule"
          onChange={onChange}
          options={termOptions}
          type="checkbox-group"
          value={invoiceTerms}
        />
      </TermsContainer>

      <LoaderButton isLoading={saving} onClick={saveTerms}>
        Save Terms
      </LoaderButton>
    </S_SelectTerms>
  )
}
